import * as React from "react";
import {
  Box,
  Button,
  Field as ChakraField,
  HStack,
  Input,
  Text,
  VStack,
  RadioGroup,
} from "@chakra-ui/react";
import {
  Formik,
  Form,
  Field,
  FieldProps,
  FormikProps,
  FormikHelpers,
} from "formik";

import { BbpsComplainLayout } from "layouts/BbpsComplainLayout/BbpsComplainLayout";
import { bbpsComplaintService } from "services/api.services/bbpsComplaint/bbpsComplaintService";
import {
  TransactionStatusRequest,
  TransactionStatusResponsePayload,
} from "store/bbps/Complaint/bbpsComplaintTypes";

type TransactionStatusMode = "orderId" | "txnRefId";

type ViewTransactionStatusFormValues = {
  mode: TransactionStatusMode;
  orderId: string;
  txnRefId: string;
};

const FloatingInputField: React.FC<
  FieldProps & {
    label: string;
    maxLength?: number;
    required?: boolean;
  }
> = ({ label, field, form, maxLength, required }) => {
  const [focus, setFocus] = React.useState(false);
  const value = String(field.value ?? "");
  const active = focus || value.length > 0;

  const error =
    (form.touched as any)?.[field.name] && (form.errors as any)?.[field.name]
      ? String((form.errors as any)[field.name])
      : undefined;

  return (
    <ChakraField.Root invalid={!!error}>
      <Box position="relative">
        <Input
          {...field}
          value={value}
          maxLength={maxLength}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            field.onBlur(e);
          }}
          h="52px"
          pt="18px"
          bg="white"
          borderColor="blackAlpha.200"
          _hover={{ borderColor: "blackAlpha.300" }}
          _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px" }}
          required={required}
          autoComplete="new-password"
        />

        <Text
          position="absolute"
          left="12px"
          top={active ? "8px" : "50%"}
          transform={active ? "translateY(0)" : "translateY(-50%)"}
          fontSize={active ? "xs" : "sm"}
          color={active ? "gray.600" : "gray.500"}
          pointerEvents="none"
          transition="all 120ms ease"
          bg="white"
          px="6px"
        >
          {label}
        </Text>
      </Box>

      {error ? <ChakraField.ErrorText>{error}</ChakraField.ErrorText> : null}
    </ChakraField.Root>
  );
};

const Row: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <HStack justify="space-between">
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="gray.800"
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  );
};

export const ViewTransactionStatusPage: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<TransactionStatusResponsePayload | null>(null);
  const [error, setError] = React.useState<string>("");

  const initialValues: ViewTransactionStatusFormValues = React.useMemo(
    () => ({
      mode: "orderId",
      orderId: "",
      txnRefId: "",
    }),
    [],
  );

  const validate = (values: ViewTransactionStatusFormValues) => {
    const errors: Partial<Record<keyof ViewTransactionStatusFormValues, string>> =
      {};
    const mode = values.mode;
    const orderId = values.orderId?.trim() ?? "";
    const txnRefId = values.txnRefId?.trim() ?? "";

    // Validate based on mode
    if (mode === "orderId") {
      if (!orderId) errors.orderId = "Order ID is required.";
      if (orderId.length > 30)
        errors.orderId = "Order ID must be <= 30 characters.";
    } else {
      if (!txnRefId) errors.txnRefId = "Transaction Reference ID is required.";
      if (txnRefId.length > 50)
        errors.txnRefId = "Transaction Reference ID must be <= 50 characters.";
    }

    return errors;
  };

  const onSubmit = async (
    values: ViewTransactionStatusFormValues,
    actions: FormikHelpers<ViewTransactionStatusFormValues>,
  ) => {
    if (loading) {
      actions.setSubmitting(false);
      return;
    }

    setLoading(true);
    setResult(null);
    setError("");

    try {
      const payload: TransactionStatusRequest =
        values.mode === "orderId"
          ? { orderId: values.orderId.trim() }
          : { txnReferenceId: values.txnRefId.trim() };

      const res = await bbpsComplaintService.transactionStatus(payload);

      if (res?.data?.status && res?.data?.data) {
        setResult(res.data.data);
      } else {
        setError(res?.data?.message || "Failed to fetch transaction status");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  const formatAmount = (amountInPaise?: string) => {
    if (!amountInPaise) return "0.00";
    const amount = parseFloat(amountInPaise) / 100;
    return amount.toFixed(2);
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <BbpsComplainLayout title="View Transaction Status" backTo="/bbps">
      <Formik<ViewTransactionStatusFormValues>
        initialValues={initialValues}
        validate={validate}
        validateOnBlur
        validateOnChange
        onSubmit={onSubmit}
      >
        {(fp: FormikProps<ViewTransactionStatusFormValues>) => {
          const canSubmit =
            fp.isValid &&
            !loading &&
            (fp.values.mode === "orderId"
              ? fp.values.orderId.trim().length > 0
              : fp.values.txnRefId.trim().length > 0);

          return (
            <Form>
              <VStack align="stretch" gap={4}>
                {/* Mode Selection */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Check Transaction Status By
                  </Text>
                  <RadioGroup.Root
                    value={fp.values.mode}
                    onValueChange={(details) => {
                      const newMode = details.value as TransactionStatusMode;
                      fp.setFieldValue("mode", newMode);

                      // Clear input fields when mode changes
                      fp.setFieldValue("orderId", "");
                      fp.setFieldValue("txnRefId", "");

                      // Reset touched state for clean UX
                      fp.setTouched({});

                      // Clear any previous results
                      setResult(null);
                      setError("");
                    }}
                  >
                    <HStack gap={6}>
                      <RadioGroup.Item value="orderId">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          Order ID
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                      <RadioGroup.Item value="txnRefId">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          Transaction Ref ID
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                    </HStack>
                  </RadioGroup.Root>
                </Box>

                {/* Order ID or TxnRefId based on mode */}
                {fp.values.mode === "orderId" ? (
                  <Field name="orderId">
                    {(p: FieldProps) => (
                      <FloatingInputField
                        {...p}
                        label="Order ID"
                        maxLength={30}
                        required
                      />
                    )}
                  </Field>
                ) : (
                  <Field name="txnRefId">
                    {(p: FieldProps) => (
                      <FloatingInputField
                        {...p}
                        label="Transaction Reference ID"
                        maxLength={50}
                        required
                      />
                    )}
                  </Field>
                )}

                <Button
                  type="submit"
                  h="48px"
                  rounded="lg"
                  loading={loading}
                  disabled={!canSubmit}
                >
                  Check Status
                </Button>

                {error && (
                  <Box
                    p={3}
                    rounded="lg"
                    bg="red.50"
                    borderWidth="1px"
                    borderColor="red.100"
                  >
                    <Text fontWeight="semibold" color="red.700">
                      {error}
                    </Text>
                  </Box>
                )}

                {result && (
                  <Box
                    p={4}
                    rounded="xl"
                    bg="blue.50"
                    borderWidth="1px"
                    borderColor="blue.100"
                  >
                    <Text fontWeight="bold" color="blue.800" mb={3}>
                      Transaction Details
                    </Text>

                    <VStack align="stretch" gap={2}>
                      <Row label="Ref ID" value={result.refId} />
                      <Row label="Transaction Status" value={result.txnStatus} />
                      <Row
                        label="Response Code"
                        value={result.additionalParams?.responseCode}
                      />
                      <Row
                        label="Response Reason"
                        value={result.additionalParams?.responseReason}
                      />
                      <Row
                        label="Message ID"
                        value={result.additionalParams?.msgId}
                      />
                      <Row
                        label="Mobile"
                        value={result.additionalParams?.customerdetails?.mobile}
                      />

                      {result.additionalParams?.txnlist?.txndetail && (
                        <>
                          <Box mt={2} pt={2} borderTopWidth="1px" borderColor="blue.200">
                            <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>
                              Transaction Details
                            </Text>
                          </Box>
                          <Row
                            label="Txn Reference ID"
                            value={result.additionalParams.txnlist.txndetail.txnReferenceId}
                          />
                          <Row
                            label="Amount"
                            value={`₹${formatAmount(result.additionalParams.txnlist.txndetail.amount)}`}
                          />
                          <Row
                            label="Approval Ref"
                            value={result.additionalParams.txnlist.txndetail.approvalRefNum}
                          />
                          <Row
                            label="Biller ID"
                            value={result.additionalParams.txnlist.txndetail.billerId}
                          />
                          <Row
                            label="Bill Number"
                            value={result.additionalParams.txnlist.txndetail.billNumber}
                          />
                          <Row
                            label="Payment Ref ID"
                            value={result.additionalParams.txnlist.txndetail.paymentRefId}
                          />
                          <Row
                            label="Transaction Date"
                            value={formatDateTime(result.additionalParams.txnlist.txndetail.txnDate)}
                          />
                          <Row
                            label="Agent ID"
                            value={result.additionalParams.txnlist.txndetail.agentId}
                          />
                          <Row
                            label="MTI"
                            value={result.additionalParams.txnlist.txndetail.mti}
                          />

                          {/* Compliance Info */}
                          {(result.additionalParams.txnlist.txndetail.complianceRespCd ||
                            result.additionalParams.txnlist.txndetail.complianceReason) && (
                            <>
                              <Box mt={2} pt={2} borderTopWidth="1px" borderColor="blue.200">
                                <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>
                                  Compliance Info
                                </Text>
                              </Box>
                              <Row
                                label="Compliance Code"
                                value={result.additionalParams.txnlist.txndetail.complianceRespCd}
                              />
                              <Row
                                label="Compliance Reason"
                                value={result.additionalParams.txnlist.txndetail.complianceReason}
                              />
                            </>
                          )}

                          {/* Dispute Info */}
                          {(result.additionalParams.txnlist.txndetail.disputeId ||
                            result.additionalParams.txnlist.txndetail.disputeStatus) && (
                            <>
                              <Box mt={2} pt={2} borderTopWidth="1px" borderColor="blue.200">
                                <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>
                                  Dispute Info
                                </Text>
                              </Box>
                              <Row
                                label="Dispute ID"
                                value={result.additionalParams.txnlist.txndetail.disputeId}
                              />
                              <Row
                                label="Dispute Status"
                                value={result.additionalParams.txnlist.txndetail.disputeStatus}
                              />
                              <Row
                                label="Dispute Type"
                                value={result.additionalParams.txnlist.txndetail.disputeType}
                              />
                              <Row
                                label="Dispute Date"
                                value={formatDateTime(result.additionalParams.txnlist.txndetail.disputeDate)}
                              />
                              <Row
                                label="Dispute Amount"
                                value={result.additionalParams.txnlist.txndetail.disputeAmount 
                                  ? `₹${formatAmount(result.additionalParams.txnlist.txndetail.disputeAmount)}`
                                  : undefined}
                              />
                            </>
                          )}

                          {/* CA Info */}
                          {(result.additionalParams.txnlist.txndetail.caId ||
                            result.additionalParams.txnlist.txndetail.caStatus) && (
                            <>
                              <Box mt={2} pt={2} borderTopWidth="1px" borderColor="blue.200">
                                <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>
                                  CA Info
                                </Text>
                              </Box>
                              <Row
                                label="CA ID"
                                value={result.additionalParams.txnlist.txndetail.caId}
                              />
                              <Row
                                label="CA Status"
                                value={result.additionalParams.txnlist.txndetail.caStatus}
                              />
                              <Row
                                label="CA Date"
                                value={formatDateTime(result.additionalParams.txnlist.txndetail.caDate)}
                              />
                              <Row
                                label="CA Amount"
                                value={result.additionalParams.txnlist.txndetail.caAmount}
                              />
                              <Row
                                label="CA Penalty"
                                value={result.additionalParams.txnlist.txndetail.caPenalty}
                              />
                            </>
                          )}
                        </>
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Form>
          );
        }}
      </Formik>
    </BbpsComplainLayout>
  );
};

export default ViewTransactionStatusPage;
